import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";

const AUTH_DATA_KEY = "auth_data";

// Create a context to share auth state across all components
const AuthDataContext = createContext(null);

// Provider component that wraps the app
export const AuthDataProvider = ({ children }) => {
  const [authData, setAuthData] = useState({
    privyDid: null,
    phoneNumber: null,
    email: null,
    linkedAccounts: [],
  });
  const [isLoaded, setIsLoaded] = useState(false);

  // Load auth data from AsyncStorage on mount
  useEffect(() => {
    const loadAuthData = async () => {
      try {
        const storedAuthData = await AsyncStorage.getItem(AUTH_DATA_KEY);
        if (storedAuthData) {
          const parsedData = JSON.parse(storedAuthData);
          setAuthData(parsedData);
        }
      } catch (error) {
        console.error("Error loading auth data:", error);
      } finally {
        setIsLoaded(true);
      }
    };

    loadAuthData();
  }, []);

  // Save auth data to AsyncStorage
  const saveAuthData = useCallback(async (newAuthData) => {
    try {
      // Only save if data actually changed
      const currentData = await AsyncStorage.getItem(AUTH_DATA_KEY);
      const currentParsed = currentData ? JSON.parse(currentData) : null;

      if (
        currentParsed?.privyDid === newAuthData?.privyDid &&
        currentParsed?.phoneNumber === newAuthData?.phoneNumber &&
        currentParsed?.email === newAuthData?.email
      ) {
        return;
      }

      await AsyncStorage.setItem(AUTH_DATA_KEY, JSON.stringify(newAuthData));
      setAuthData(newAuthData); // This now updates ALL consumers
    } catch (error) {
      console.error("Error saving auth data:", error);
    }
  }, []);

  // Delete auth data (logout)
  const deleteAuthData = useCallback(async () => {
    try {
      await AsyncStorage.removeItem(AUTH_DATA_KEY);
      setAuthData({ privyDid: null, phoneNumber: null, email: null, linkedAccounts: [] });
    } catch (error) {
      console.error("Error deleting auth data:", error);
    }
  }, []);

  // Update linked accounts from Privy user data
  const updateLinkedAccounts = useCallback(async (privyUser) => {
    if (!privyUser?.linked_accounts) return;

    const linkedAccounts = privyUser.linked_accounts.map(account => ({
      type: account.type,
      address: account.address || account.phoneNumber || account.number,
      verified: account.verified || false,
    }));

    const emailAccount = privyUser.linked_accounts.find(acc => acc.type === "email");
    const phoneAccount = privyUser.linked_accounts.find(acc => acc.type === "phone");

    const updatedAuthData = {
      ...authData,
      linkedAccounts,
      email: emailAccount?.address || null,
      phoneNumber: phoneAccount?.phoneNumber || phoneAccount?.number || null,
    };

    await saveAuthData(updatedAuthData);
  }, [authData, saveAuthData]);

  const value = {
    authData,
    saveAuthData,
    deleteAuthData,
    updateLinkedAccounts,
    isLoaded,
  };

  return (
    <AuthDataContext.Provider value={value}>
      {children}
    </AuthDataContext.Provider>
  );
};

// Hook to consume the auth context
export const useAuthData = () => {
  const context = useContext(AuthDataContext);
  if (!context) {
    throw new Error("useAuthData must be used within an AuthDataProvider");
  }
  return context;
};
